import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { persistQueryClient } from '@tanstack/react-query-persist-client';

// L1 — In-Memory cache (React Query)
// L2 — Persistenza localStorage con TTL 24h (riavvio istantaneo)
// L3 — Service Worker (cache risorse statiche)
// L4 — Background Sync API — attivata dopo login (F02)

const ONE_MINUTE = 60 * 1000;
const ONE_HOUR = 60 * ONE_MINUTE;
const ONE_DAY = 24 * ONE_HOUR;

// Handler globale errore. Viene impostato dal ToastProvider al boot.
let globalErrorHandler: ((message: string) => void) | null = null;
export function setGlobalErrorHandler(fn: (message: string) => void): void {
  globalErrorHandler = fn;
}

function handleError(err: unknown, silent: boolean = false): void {
  if (silent) return;
  const msg =
    (err as { response?: { data?: { error?: string } } })?.response?.data?.error
    ?? (err as { message?: string })?.message
    ?? 'Errore imprevisto';
  // Non mostrare toast per 401 (gestito dal refresh flow)
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 401) return;
  globalErrorHandler?.(msg);
}

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (err, query) => {
        // Permette a singole query di opt-out via meta.silent
        const silent = (query.meta?.silent as boolean) ?? false;
        handleError(err, silent);
      },
    }),
    mutationCache: new MutationCache({
      onError: (err, _vars, _ctx, mutation) => {
        const silent = (mutation.meta?.silent as boolean) ?? false;
        // Skip handler se la mutation ha gia' un onError proprio
        if (mutation.options.onError) return;
        handleError(err, silent);
      },
    }),
    defaultOptions: {
      queries: {
        // staleTime corto: dopo create/update vediamo subito i nuovi dati.
        // Le mutation invalidano comunque per cache miss diretto.
        staleTime: 30 * 1000,
        gcTime: 10 * ONE_MINUTE,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
        retry: 2,
      },
      mutations: {
        retry: 1,
      },
    },
  });
}

export function enableQueryPersistence(queryClient: QueryClient): void {
  if (typeof window === 'undefined') return;

  const persister = createSyncStoragePersister({
    storage: window.localStorage,
    key: 'vvf.query-cache',
    throttleTime: 1000,
  });

  persistQueryClient({
    queryClient,
    persister,
    maxAge: ONE_DAY,
    // Bump quando lo schema dei dati cached cambia o quando devi forzare
    // l'invalidazione su tutti i client (es. recover da cache corrotta).
    buster: 'v0.2.0',
  });
}
